import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingApi } from '../services/api';
import { PageHead, Card, Badge, Btn, Spinner, Toast } from '../components/UI';

export default function BookingDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    load();
  }, [id]);

  async function load() {
    if (!id) {
      setError('Invalid booking id.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await bookingApi.getById(id);
      setBooking(res.data?.data || null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to load Booking LE details.';
      setError(msg);
      setToast({ msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem', gap: 10 }}>
        <Spinner size={24} />
        <span style={{ color: 'var(--text2)' }}>Loading Booking LE details...</span>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page-anim">
        <PageHead
          title="Booking LE Details"
          subtitle={error || 'Booking LE was not found.'}
          actions={<Btn variant="ghost" onClick={() => navigate('/bookings')}>Back to Booking LE</Btn>}
        />
      </div>
    );
  }

  const assets = booking.assets || booking.Assets || [];
  const slots = booking.slots || booking.Slots || [];

  return (
    <div className="page-anim">
      <PageHead
        title="Booking LE Details"
        subtitle={`Reference ${booking.bookingReference || booking.ref || '-'}`}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" onClick={() => navigate('/bookings')}>Back</Btn>
          </div>
        }
      />

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
          <Detail label="Event" value={booking.eventName || '-'} />
          <Detail label="Campus" value={booking.campusName || '-'} />
          <Detail label="Area" value={booking.eventAreaName || '-'} />
          <Detail label="Date" value={booking.eventDate || '-'} />
          <Detail label="Time" value={`${booking.startTime || '-'} - ${booking.endTime || '-'}`} />
          <Detail label="Expected Attendees" value={booking.expectedAttendees ?? 0} />
          <Detail label="Who Booked" value={booking.createdBy || '-'} />
          <Detail label="Booked Email" value={booking.createdByEmail || '-'} />
          <Detail label="Submitted At" value={booking.submittedAt ? String(booking.submittedAt).replace('T', ' ').slice(0, 19) : '-'} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
            <Badge status={booking.status || 'Pending'} />
          </div>
        </div>
      </Card>

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Purpose</div>
        <div style={{ fontSize: 14, color: 'var(--text2)' }}>{booking.purpose || '-'}</div>
      </Card>

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Selected Slots</div>
        {slots.length === 0 ? (
          <div style={{ fontSize: 14, color: 'var(--text2)' }}>No slots available.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {slots.map((s, idx) => (
              <span key={idx} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', padding: '4px 8px', borderRadius: 16, fontSize: 12, fontWeight: 600 }}>
                {s.slotStart || s.SlotStart} - {s.slotEnd || s.SlotEnd}
              </span>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Required Assets</div>
        {assets.length === 0 ? (
          <div style={{ fontSize: 14, color: 'var(--text2)' }}>No assets requested.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {assets.map((a, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
                <span style={{ fontWeight: 600 }}>{a.assetName || `Asset #${a.assetID || a.assetId}`}</span>
                <span style={{ color: 'var(--text2)' }}>x {a.quantityRequested ?? a.quantity ?? 0}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
    </div>
  );
}
